import React, { useEffect, useMemo, useState } from 'react';
import { useValidatorsStore } from '@/stores/validators';
import { useUiStore } from '@/stores/ui';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { FlaskConical, Sparkles, Loader2, AlertCircle, CheckCircle, File as FileIcon, AlertTriangle, Wand2 } from 'lucide-react';
import { CoverageInfo } from '@/core/state-shape';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { http } from '@/lib/http';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { useProjectsStore } from '@/stores/projects';

// Helper to robustly extract numeric value from inconsistent data structure
const getNumericValue = (value: any): number | null => {
  if (typeof value === 'number') {
    return value;
  }
  if (value && typeof value === 'object' && typeof value.parsedValue === 'number') {
    return value.parsedValue;
  }
  return null;
};

type Failure = {
  type: 'Test';
  status: 'failed' | 'flaky';
  name: string;
  file: string;
  line?: number;
  message: string;
};

// Helper to calculate project-wide coverage summary
const calculateOverallCoverage = (files: any[]) => {
  const summary = {
    lines: { total: 0, covered: 0 },
    functions: { total: 0, covered: 0 },
    branches: { total: 0, covered: 0 },
  };

  files.forEach(file => {
    summary.lines.total += getNumericValue(file.summary.lines?.total) ?? 0;
    summary.lines.covered += getNumericValue(file.summary.lines?.covered) ?? 0;
    summary.functions.total += getNumericValue(file.summary.functions?.total) ?? 0;
    summary.functions.covered += getNumericValue(file.summary.functions?.covered) ?? 0;
    summary.branches.total += getNumericValue(file.summary.branches?.total) ?? 0;
    summary.branches.covered += getNumericValue(file.summary.branches?.covered) ?? 0;
  });

  const calculatePct = (covered: number, total: number) => (total > 0 ? (covered / total) * 100 : 0);

  return {
    linesPct: calculatePct(summary.lines.covered, summary.lines.total),
    functionsPct: calculatePct(summary.functions.covered, summary.functions.total),
    branchesPct: calculatePct(summary.branches.covered, summary.branches.total),
  };
};

const CoverageBar = ({ label, percentage }: { label: string, percentage: number }) => {
  let colorClass = 'bg-red-500';
  if (percentage >= 80) colorClass = 'bg-green-500';
  else if (percentage >= 50) colorClass = 'bg-yellow-500';

  return (
    <div className="grid grid-cols-3 items-center gap-4">
      <div className="text-sm font-medium">{label}</div>
      <div className="col-span-2">
        <div className="flex items-center gap-2">
          <Progress value={percentage} className={cn("[&>div]:", colorClass)} />
          <span className="text-sm font-mono tabular-nums">{percentage.toFixed(1)}%</span>
        </div>
      </div>
    </div>
  );
};

export default function TestsPage() {
  const { setHeaderTitle } = useUiStore();
  const { runFullCoverage, progress, poll, results, loadResultsForProject } = useValidatorsStore();
  const { activeProjectId } = useProjectsStore();
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const latestResults = results.latest;

  useEffect(() => {
    setHeaderTitle('Tests & Coverage');
  }, [setHeaderTitle]);

  useEffect(() => {
    if (activeProjectId) {
      setIsLoading(true);
      loadResultsForProject(activeProjectId).finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [activeProjectId, loadResultsForProject]);

  useEffect(() => {
    if (!activeRunId) return;
    const interval = setInterval(() => {
      const currentRun = useValidatorsStore.getState().progress[activeRunId];
      if (currentRun?.status !== 'running') {
        clearInterval(interval);
        setActiveRunId(null);
        if (currentRun?.status === 'complete' || currentRun?.status === 'failed') {
          toast.success("Test run complete. Refreshing data...");
          fetchSnapshot();
        }
        return;
      }
      poll(activeRunId);
    }, 2000);

    return () => clearInterval(interval);
  }, [activeRunId, poll]);

  const allFilesWithCoverage = useMemo(() => {
    if (!latestResults?.coverage && !latestResults?.tests?.coverage) {
      return [];
    }
    const coverageData = latestResults.coverage || latestResults.tests?.coverage;
    const files: any[] = Object.values(coverageData || {});
    return files.sort((a, b) => a.path.localeCompare(b.path));
  }, [latestResults]);

  const overallCoverage = useMemo(() => calculateOverallCoverage(allFilesWithCoverage), [allFilesWithCoverage]);

  const activeCoverageRun = useMemo(() => {
    const runs = Object.values(progress)
      .filter(p => !p.taskId && p.status === 'running')
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    return runs[0] || null;
  }, [progress]);

  const allTestFailures = useMemo((): Failure[] => {
    if (!latestResults?.tests?.failures) return [];
    return latestResults.tests.failures.map((f: any) => ({
      type: 'Test',
      status: 'failed',
      name: f.test || 'Unknown Test',
      file: f.suite || 'Unknown Suite',
      line: f.line,
      message: f.error || 'No message provided',
    }));
  }, [latestResults]);

  const failuresByFile = useMemo(() => {
    const grouped: Record<string, Failure[]> = {};
    for (const failure of allTestFailures) {
      if (!grouped[failure.file]) {
        grouped[failure.file] = [];
      }
      grouped[failure.file].push(failure);
    }
    return Object.entries(grouped).sort(([pathA], [pathB]) => pathA.localeCompare(pathB));
  }, [allTestFailures]);
  
  const hasFailures = useMemo(() => {
      if (!latestResults?.tests) return false;
      return latestResults.tests.status === 'failed' || allTestFailures.length > 0;
  }, [latestResults, allTestFailures]);

  const handleFixTests = async (filePath: string) => {
    if (!latestResults?.runId) {
      toast.error("Cannot fix tests", { description: "The latest test run ID is missing." });
      return;
    }

    const toastId = toast.loading(`Starting AI fix for ${filePath}...`);

    try {
      const { taskId } = await http<{ taskId: string }>('/api/workspace/fix_tests', {
        method: 'POST',
        body: JSON.stringify({
          testFilePath: filePath,
          runId: latestResults.runId,
        }),
      });
      toast.dismiss(toastId);
      if (taskId) {
        toast.success("Test fix task started successfully.");
        navigate(`/pages/generation-progress/?taskId=${taskId}`);
      } else {
        throw new Error("Did not receive a task ID from the server.");
      }
    } catch (error: any) {
      toast.dismiss(toastId);
      toast.error("Failed to start test fix task", { description: error.message });
    }
  };

  const handleRunCoverage = async () => {
    const runId = await runFullCoverage();
    setActiveRunId(runId);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-4 lg:p-6 h-full flex items-center justify-center">
        <div className="flex items-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="ml-2">Loading test results...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 lg:p-6 space-y-6 overflow-auto h-full">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="hidden md:block">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FlaskConical /> Tests &amp; Coverage
          </h1>
          <p className="text-muted-foreground">
            Run tests and view code coverage for the entire project.
          </p>
        </div>
        <Button onClick={handleRunCoverage} disabled={!!activeCoverageRun}>
          {activeCoverageRun ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Running...</>
          ) : (
            <><Sparkles className="mr-2 h-4 w-4" /> Run All Tests &amp; Coverage</>
          )}
        </Button>
      </header>
      
      {activeCoverageRun && (
        <Card>
          <CardHeader>
            <CardTitle>Test Run in Progress</CardTitle>
            <CardDescription className="capitalize">
              Phase: {activeCoverageRun.phase?.replace(/_/g, ' ') || 'Starting...'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Progress value={activeCoverageRun.percent || 20} />
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="coverage" className="w-full">
        <TabsList>
          <TabsTrigger value="coverage">Coverage</TabsTrigger>
          <TabsTrigger value="failures">
            Failures 
            {latestResults?.tests && (
              <Badge variant={hasFailures ? "destructive" : "secondary"} className="ml-2">
                {getNumericValue(latestResults.tests.failed) ?? 0}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="coverage">
            {allFilesWithCoverage.length === 0 && !activeCoverageRun && (
            <Card className="text-center mt-4">
                <CardHeader>
                    <CardTitle className="flex items-center justify-center gap-2">
                        <AlertCircle className="h-5 w-5 text-muted-foreground" />
                        No Coverage Data
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-muted-foreground">Run a full test and coverage scan to see results here.</p>
                </CardContent>
            </Card>
            )}

            {allFilesWithCoverage.length > 0 && (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mt-4">
                    <Card className="lg:col-span-1">
                    <CardHeader><CardTitle>Project Coverage Summary</CardTitle></CardHeader>
                    <CardContent className="space-y-4">
                        <CoverageBar label="Lines" percentage={overallCoverage.linesPct} />
                        <CoverageBar label="Functions" percentage={overallCoverage.functionsPct} />
                        <CoverageBar label="Branches" percentage={overallCoverage.branchesPct} />
                    </CardContent>
                    </Card>

                    <Card className="md:col-span-2 lg:col-span-2">
                    <CardHeader><CardTitle>Coverage by File</CardTitle></CardHeader>
                    <CardContent>
                        <ScrollArea className="h-[400px]">
                        <Table>
                            <TableHeader>
                            <TableRow>
                                <TableHead>File</TableHead>
                                <TableHead className="text-right w-28">Line Cov.</TableHead>
                                <TableHead className="text-right w-28">Branch Cov.</TableHead>
                            </TableRow>
                            </TableHeader>
                            <TableBody>
                            {allFilesWithCoverage.map(file => (
                                <TableRow key={file.path}>
                                  <TableCell className="font-mono text-xs">{file.path}</TableCell>
                                  <TableCell className="text-right font-mono text-xs tabular-nums">
                                    {getNumericValue(file.summary.lines?.pct)?.toFixed(1) ?? 'N/A'}%
                                  </TableCell>
                                  <TableCell className="text-right font-mono text-xs tabular-nums">
                                    {getNumericValue(file.summary.branches?.pct)?.toFixed(1) ?? 'N/A'}%
                                  </TableCell>
                                </TableRow>
                            ))}
                            </TableBody>
                        </Table>
                        </ScrollArea>
                    </CardContent>
                    </Card>
                </div>
            )}
        </TabsContent>
        <TabsContent value="failures">
          <div className="mt-4">
          {failuresByFile.length > 0 ? (
            <div className="space-y-2">
                {failuresByFile.map(([filePath, failures]) => (
                  <Card key={filePath} className="overflow-hidden">
                    <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 p-4 bg-muted/50">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <FileIcon className="h-4 w-4" />
                        <span className="font-mono text-sm font-semibold truncate">{filePath}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => handleFixTests(filePath)}>
                          <Wand2 className="h-4 w-4 mr-1" />
                          Fix with AI
                        </Button>
                        <Badge variant="destructive" className="shrink-0">{failures.length} failure(s)</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className={cn(isMobile ? 'p-2' : 'p-0')}>
                      {isMobile ? (
                        <div className="space-y-2">
                          {failures.map((f, index) => (
                            <div key={index} className="p-2 border rounded bg-background">
                              <p className="font-semibold text-sm break-all">{f.name}</p>
                              <p className="text-xs text-muted-foreground font-mono mt-1 break-all">{f.message}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Test Name</TableHead>
                              <TableHead>Message</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                          {failures.map((f, index) => (
                            <TableRow key={index}>
                              <TableCell className="font-semibold">{f.name}</TableCell>
                            <TableCell className="text-xs text-muted-foreground font-mono">{f.message}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      </Table>
                    )}
                                      </CardContent>

                </Card>
              ))}
            </div>
          ) : hasFailures ? (
             <div className="text-center py-12 text-muted-foreground">
                <AlertTriangle className="mx-auto h-12 w-12 text-destructive" />
                <p className="mt-4 font-semibold">Tests Failed</p>
                <p>The test run reported failures, but no specific error details were provided.</p>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
              <p className="mt-4 font-semibold">All tests passed!</p>
              <p>No test failures were found in the last run.</p>
            </div>
          )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}